/**
 * Pure logic for the in-thread shortlist search modal.
 *
 * Render-free ON PURPOSE, the same split as `threadStreamModel`. Everything a
 * reviewer would want to mutate — the endpoint fork, the grouping, the
 * inventory_serve_pricing gate, the dedupe — lives here and is tested by calling it, not
 * by driving a Dialog. A modal test that must open MUI, wait for a fetch and
 * click a checkbox to prove that a stale property is unselectable is a test
 * nobody will maintain, and this repo's own landmines (CRA `resetMocks` wiping
 * factory mocks, a silent jest hang meaning a render loop) make that harness
 * exactly the kind future seats route around.
 */

export interface ShortlistSearchFilters {
  maxPrice: string;
  minBeds: string;
  neighborhoods: string[];
  availableOnly: boolean;
}

/** One row as the search endpoints return it (the fields this modal reads). */
export interface SearchRow {
  property_id: string;
  property_name: string;
  neighborhood?: string | null;
  s3_image_url?: string | null;
  /** 🔴 The freshness gate. `true` means we may show and shortlist pricing. */
  inventory_serve_pricing?: boolean | null;
  floor_plan_id?: string | null;
  model_name?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  square_feet?: number | null;
  current_price?: number | null;
  status?: string | null;
  availability_date?: string | null;
}

export interface FloorPlanChoice {
  floorPlanId: string;
  modelName: string;
  bedrooms: number | null;
  bathrooms: number | null;
  squareFeet: number | null;
  rentLow: number | null;
  rentHigh: number | null;
  availableUnitCount: number;
  availabilityDates: string[];
}

export interface PropertyCard {
  propertyId: string;
  propertyName: string;
  neighborhood: string | null;
  imageUrl: string | null;
  rentLow: number | null;
  rentHigh: number | null;
  pricingCurrent: boolean;
  floorplanIdsComplete: boolean;
  /** False when pricing or durable floorplan ancestry is unknown. */
  selectable: boolean;
  unitCount: number;
  floorPlans: FloorPlanChoice[];
}

export const SEARCH_ENDPOINT_ADVANCED = "search/advanced-filters";
export const SEARCH_ENDPOINT_COMPREHENSIVE = "search/comprehensive";

/**
 * Endpoint + query params for a filter set.
 *
 * The fork MIRRORS `ApartmentSearch.tsx:2655-2667` deliberately: neighborhoods
 * require `advanced-filters`, and `available_only` is only a parameter on the
 * comprehensive endpoint. Two callers computing this differently would produce
 * two different result sets for the same filters, which is the kind of
 * divergence nobody notices until a client is sent a property that should not
 * have been offered.
 */
export function buildSearchParams(filters: ShortlistSearchFilters): {
  endpoint: string;
  params: URLSearchParams;
} {
  const params = new URLSearchParams();
  const useAdvanced = filters.neighborhoods.length > 0;

  if (filters.maxPrice.trim()) params.set("max_price", filters.maxPrice.trim());
  if (filters.minBeds.trim()) params.set("min_beds", filters.minBeds.trim());
  if (useAdvanced) {
    filters.neighborhoods.forEach((n) => params.append("neighborhoods", n));
  } else {
    // Only the comprehensive endpoint takes this — see the fork above.
    params.set("available_only", String(filters.availableOnly));
  }
  params.set("sort_by", "price"); // v1 has no sort control; fixed, not absent.

  return {
    endpoint: useAdvanced ? SEARCH_ENDPOINT_ADVANCED : SEARCH_ENDPOINT_COMPREHENSIVE,
    params,
  };
}

function currentPriceOf(row: SearchRow): number | null {
  return typeof row.current_price === "number" && Number.isFinite(row.current_price)
    ? row.current_price
    : null;
}

/**
 * 🔴 PKT-0943: the price a client could actually rent today. The search
 * endpoints return a row per unit INCLUDING `status: "unavailable"` units
 * (advanced-filters has no status filter), so a range built from every row
 * showed leased units' prices ("$1,339–$1,398 · 0 available" at Apex A2 when the
 * only open A2 was $1,434). Only `status === "available"` rows may carry price.
 */
function rentablePriceOf(row: SearchRow): number | null {
  return row.status === "available" ? currentPriceOf(row) : null;
}

const formatDollars = (value: number) => `$${value.toLocaleString("en-US")}`;

/**
 * Price + availability text for one plan row in the picker. A plan with no open
 * unit within the searched budget shows NO price — "none open under $X right
 * now" — never the prices of units someone else has already leased.
 */
export function planPriceLabel(
  floorPlan: Pick<FloorPlanChoice, "rentLow" | "rentHigh" | "availableUnitCount">,
  maxPrice: string
): string {
  if (floorPlan.availableUnitCount <= 0) {
    const budget = Number(String(maxPrice).replace(/[$,\s]/g, ""));
    return Number.isFinite(budget) && budget > 0
      ? `none open under ${formatDollars(budget)} right now`
      : "none open right now";
  }
  const parts: string[] = [];
  if (floorPlan.rentLow !== null) {
    parts.push(
      floorPlan.rentHigh !== null && floorPlan.rentHigh !== floorPlan.rentLow
        ? `${formatDollars(floorPlan.rentLow)}–${formatDollars(floorPlan.rentHigh)}`
        : formatDollars(floorPlan.rentLow)
    );
  }
  parts.push(`${floorPlan.availableUnitCount} available`);
  return parts.join(" · ");
}

/**
 * Collapse unit/floorplan rows into one card per property.
 *
 * The endpoints return a row per unit, so a 40-unit building would otherwise be
 * 40 identical-looking results in a modal an agent is scanning mid-conversation.
 *
 * 🔴 `inventory_serve_pricing !== true` makes a card UNSELECTABLE rather than hidden —
 * the same rule as `buildFreshShortlistSelections.ts:41`. Hiding it would leave
 * the agent wondering why a property they know exists is missing; showing it
 * unselectable says WHY. Note the strict `!== true`: `undefined` and `null` are
 * both "we do not know", and not-knowing is not permission.
 */
export function groupUnitsByProperty(rows: SearchRow[]): PropertyCard[] {
  const byId = new Map<string, PropertyCard>();

  for (const row of rows) {
    if (!row?.property_id) continue;
    const rent = rentablePriceOf(row);
    const existing = byId.get(row.property_id);

    if (!existing) {
      byId.set(row.property_id, {
        propertyId: row.property_id,
        propertyName: row.property_name || "(unnamed property)",
        neighborhood: row.neighborhood ?? null,
        imageUrl: row.s3_image_url ?? null,
        rentLow: rent,
        rentHigh: rent,
        pricingCurrent: row.inventory_serve_pricing === true,
        floorplanIdsComplete:
          typeof row.floor_plan_id === "string" && row.floor_plan_id.trim().length > 0,
        selectable:
          row.inventory_serve_pricing === true &&
          typeof row.floor_plan_id === "string" &&
          row.floor_plan_id.trim().length > 0,
        unitCount: 1,
        floorPlans: [],
      });
    } else {
      existing.unitCount += 1;
      if (rent !== null) {
        existing.rentLow = existing.rentLow === null ? rent : Math.min(existing.rentLow, rent);
        existing.rentHigh = existing.rentHigh === null ? rent : Math.max(existing.rentHigh, rent);
      }
      // ANY stale/unknown or ID-less row taints the property. Not knowing the
      // exact current floorplan ancestry is not permission to persist it.
      if (
        row.inventory_serve_pricing !== true
      ) {
        existing.pricingCurrent = false;
        existing.selectable = false;
      }
      if (typeof row.floor_plan_id !== "string" || !row.floor_plan_id.trim()) {
        existing.floorplanIdsComplete = false;
        existing.selectable = false;
      }
    }

    const card = byId.get(row.property_id)!;
    const floorPlanId = typeof row.floor_plan_id === "string" ? row.floor_plan_id.trim() : "";
    if (!floorPlanId) continue;
    let floorPlan = card.floorPlans.find((choice) => choice.floorPlanId === floorPlanId);
    if (!floorPlan) {
      floorPlan = {
        floorPlanId,
        modelName: row.model_name?.trim() || "Floor plan",
        bedrooms: row.bedrooms ?? null,
        bathrooms: row.bathrooms ?? null,
        squareFeet: row.square_feet ?? null,
        rentLow: rent,
        rentHigh: rent,
        availableUnitCount: row.status === "available" ? 1 : 0,
        availabilityDates: row.availability_date ? [row.availability_date] : [],
      };
      card.floorPlans.push(floorPlan);
      continue;
    }
    if (rent !== null) {
      floorPlan.rentLow = floorPlan.rentLow === null ? rent : Math.min(floorPlan.rentLow, rent);
      floorPlan.rentHigh = floorPlan.rentHigh === null ? rent : Math.max(floorPlan.rentHigh, rent);
    }
    if (row.status === "available") floorPlan.availableUnitCount += 1;
    if (row.availability_date && !floorPlan.availabilityDates.includes(row.availability_date)) {
      floorPlan.availabilityDates.push(row.availability_date);
    }
  }

  return Array.from(byId.values());
}

export interface ExistingShortlistItem {
  id: string;
  property_id: string;
  metadata?: Record<string, unknown>;
}

export interface FloorplanSelectionMetadata extends Record<string, unknown> {
  source: "messages_shortlist_search";
  selection_mode: "floorplan";
  floor_plan_ids: string[];
  floor_plan_names: string[];
  selected_min_rent: number | null;
  selected_max_rent: number | null;
}

export interface ShortlistWritePlan {
  adds: Array<{
    propertyId: string;
    sortOrder: number;
    metadata: FloorplanSelectionMetadata;
  }>;
  updates: Array<{
    itemId: string;
    propertyId: string;
    metadata: FloorplanSelectionMetadata;
  }>;
}

/** Build one property write per selected set of durable floorplan IDs. */
export function planShortlistWrites(
  existingItems: ExistingShortlistItem[],
  cards: PropertyCard[],
  selectedFloorPlanIds: string[],
  existingItemCount: number
): ShortlistWritePlan {
  const selected = new Set(selectedFloorPlanIds.filter(Boolean));
  const existingByProperty = new Map(existingItems.map((item) => [item.property_id, item]));
  const adds: ShortlistWritePlan["adds"] = [];
  const updates: ShortlistWritePlan["updates"] = [];

  cards.forEach((card) => {
    const floorPlans = card.floorPlans.filter((choice) => selected.has(choice.floorPlanId));
    if (!card.selectable || floorPlans.length === 0) return;
    const prices = floorPlans.flatMap((choice) =>
      [choice.rentLow, choice.rentHigh].filter(
        (value): value is number => typeof value === "number" && Number.isFinite(value)
      )
    );
    const metadata: FloorplanSelectionMetadata = {
      source: "messages_shortlist_search",
      selection_mode: "floorplan",
      floor_plan_ids: floorPlans.map((choice) => choice.floorPlanId),
      floor_plan_names: floorPlans.map((choice) => choice.modelName),
      selected_min_rent: prices.length ? Math.min(...prices) : null,
      selected_max_rent: prices.length ? Math.max(...prices) : null,
    };
    const existing = existingByProperty.get(card.propertyId);
    if (existing) {
      updates.push({ itemId: existing.id, propertyId: card.propertyId, metadata });
    } else {
      adds.push({
        propertyId: card.propertyId,
        sortOrder: existingItemCount + adds.length,
        metadata,
      });
    }
  });

  return { adds, updates };
}

export interface ShortlistAddPlan {
  /** Properties to POST, in order, with their assigned sort_order. */
  adds: Array<{ propertyId: string; sortOrder: number }>;
  /** Already on the list — reported to the agent, never re-posted. */
  skipped: string[];
}

/**
 * Decide what to add to an existing shortlist.
 *
 * APPEND, never replace: migration 036's unique index means one active shortlist
 * per client, so the list is a running collection across the whole conversation,
 * not a per-search snapshot. Replacing would silently drop places the agent sent
 * an hour ago.
 *
 * Dedupe is client-side by design. Whether the server also rejects duplicate
 * property_ids is NOT VERIFIED — so this does not rely on it, and a duplicate
 * would at worst be skipped twice rather than double-posted.
 */
export function planShortlistAdds(
  existingPropertyIds: string[],
  selectedPropertyIds: string[],
  existingItemCount: number
): ShortlistAddPlan {
  const already = new Set(existingPropertyIds);
  const seen = new Set<string>();
  const adds: ShortlistAddPlan["adds"] = [];
  const skipped: string[] = [];

  selectedPropertyIds.forEach((id) => {
    if (!id) return;
    // Guard the selection against itself as well as against the server list: a
    // duplicate inside one selection is a UI bug, and it must not become two
    // POSTs carrying the same sort_order.
    if (already.has(id) || seen.has(id)) {
      if (already.has(id)) skipped.push(id);
      return;
    }
    seen.add(id);
    adds.push({ propertyId: id, sortOrder: existingItemCount + adds.length });
  });

  return { adds, skipped };
}

/**
 * PKT-0841 · name search. Case- and punctuation-insensitive substring match on
 * the property name, so "nomia" finds "Nomia" and "ponce city" finds
 * "Ponce City Market Apartments". An empty query keeps every card.
 */
export function normalizeName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

export function filterCardsByName(cards: PropertyCard[], query: string): PropertyCard[] {
  const q = normalizeName(query);
  if (!q) return cards;
  return cards.filter((card) => normalizeName(card.propertyName).includes(q));
}

/** A profile field as `/clients/{id}/profile` returns it (the parts read here). */
export interface ProfileFieldInput {
  field: string;
  value: unknown;
  state: "known" | "stale" | "unknown" | "cleared";
  source_label: string | null;
}

export interface CriteriaPrefill {
  filters: Pick<ShortlistSearchFilters, "maxPrice" | "minBeds" | "neighborhoods">;
  /** Areas the client named that are not a searchable neighborhood. */
  unmatchedAreas: string[];
  /** Distinct sources, e.g. ["the website form"]. */
  sources: string[];
  /** True when at least one filter was filled. */
  applied: boolean;
}

/**
 * PKT-0841 §4 (Grant approved): seed the modal from the client's OWN criteria.
 *
 * Shapes are the ones production holds (checked read-only 2026-09-23):
 * budget is a number; bedrooms is an int from the client row OR lead-form text
 * ("1 BED", "STUDIO", "3+ BEDS", "1 Bedroom"); neighborhoods are free strings,
 * some of them "Other: …" text that matches no searchable area. An unmatched
 * area is reported, never guessed into a nearby one.
 */
export function prefillFromProfile(
  fields: ProfileFieldInput[],
  availableNeighborhoods: string[],
): CriteriaPrefill {
  const byField = new Map(
    fields.filter((f) => (f.state === "known" || f.state === "stale") && f.value !== null && f.value !== undefined)
      .map((f) => [f.field, f]),
  );
  const sources = new Set<string>();
  const used = (f: ProfileFieldInput | undefined) => {
    if (f?.source_label) sources.add(f.source_label);
  };

  let maxPrice = "";
  const budget = byField.get("budget");
  const budgetNumber = typeof budget?.value === "number" ? budget.value : Number(budget?.value);
  if (budget && Number.isFinite(budgetNumber) && budgetNumber > 0) {
    maxPrice = String(Math.round(budgetNumber));
    used(budget);
  }

  let minBeds = "";
  const beds = byField.get("bedrooms");
  if (beds) {
    const text = String(beds.value).toLowerCase();
    const digit = text.match(/\d+/);
    if (digit) {
      const n = Math.min(Number(digit[0]), 4);
      if (n >= 1) {
        minBeds = String(n);
        used(beds);
      }
    }
    // Studio / 0 bedrooms: "Any" is already the minimum; nothing to fill.
  }

  const neighborhoods: string[] = [];
  const unmatchedAreas: string[] = [];
  const areasField = byField.get("neighborhoods");
  const areas = Array.isArray(areasField?.value) ? (areasField!.value as unknown[]) : [];
  const lookup = new Map(availableNeighborhoods.map((n) => [n.trim().toLowerCase(), n]));
  for (const raw of areas) {
    const area = String(raw ?? "").trim();
    if (!area) continue;
    const match = lookup.get(area.toLowerCase());
    if (match) {
      if (!neighborhoods.includes(match)) neighborhoods.push(match);
    } else {
      unmatchedAreas.push(area.replace(/^other:\s*/i, ""));
    }
  }
  if (neighborhoods.length || unmatchedAreas.length) used(areasField);

  return {
    filters: { maxPrice, minBeds, neighborhoods },
    unmatchedAreas,
    sources: Array.from(sources),
    applied: Boolean(maxPrice || minBeds || neighborhoods.length),
  };
}
